import { ApiRequestOptions } from '@api/core/ApiRequestOptions';
import { getRequestOptions } from '@api/core/utils';

import type { CodeChallengeResponseModel } from '../models/CodeChallengeResponseModel';
import type { EmptyResponseViewModel } from '../models/EmptyResponseViewModel';
import type { GetActivityViewModel } from '../models/GetActivityViewModel';
import type { PasswordResetViewModel } from '../models/PasswordResetViewModel';
import type { PasswordUpdateViewModel } from '../models/PasswordUpdateViewModel';
import type { RefreshTokenRequestViewModel } from '../models/RefreshTokenRequestViewModel';
import type { RegisterViewModel } from '../models/RegisterViewModel';
import type { TokenRequestViewModel } from '../models/TokenRequestViewModel';
import type { TokenViewModel } from '../models/TokenViewModel';
import type { CancelablePromise } from '../core/CancelablePromise';
import { request as __request } from '../core/request';

export class AuthenticationService {
  /**
   * CodeChallenge
   * Gets code challence and state params
   * @returns CodeChallengeResponseModel Success
   * @throws ApiError
   */
  public static async codeChallenge(
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<CodeChallengeResponseModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    delete cookies.token;
    const result = await __request<CodeChallengeResponseModel>({
      method: 'GET',
      url: `/auth/code-challenge`,
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      headers: getRequestOptions(headers, 'headers'),
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Token
   * Gets access token by access code
   * @returns TokenViewModel Success
   * @throws ApiError
   */
  public static async token(
    {
      body,
    }: {
      body?: TokenRequestViewModel;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<TokenViewModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    delete cookies.token;
    const result = await __request<TokenViewModel>({
      method: 'POST',
      url: `/auth/token`,
      body: body,
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      headers: getRequestOptions(headers, 'headers'),
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Logout
   * Logouts user
   * @returns any Success
   * @throws ApiError
   */
  public static async logout(
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<any> {
    const { cookies = {}, headers = {} } = options ?? {};
    delete cookies.token;
    const result = await __request<any>({
      method: 'GET',
      url: `/auth/logout`,
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      headers: getRequestOptions(headers, 'headers'),
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Token Refresh
   * Gets access token by refresh token
   * @returns TokenViewModel Success
   * @throws ApiError
   */
  public static async tokenRefresh(
    {
      body,
    }: {
      body?: RefreshTokenRequestViewModel;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<TokenViewModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    delete cookies.token;
    const result = await __request<TokenViewModel>({
      method: 'POST',
      url: `/auth/token/refresh`,
      body: body,
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      headers: getRequestOptions(headers, 'headers'),
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Register
   * Registers an user
   * @returns EmptyResponseViewModel Success
   * @throws ApiError
   */
  public static async register(
    {
      body,
    }: {
      body?: RegisterViewModel;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<EmptyResponseViewModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    delete cookies.token;
    const result = await __request<EmptyResponseViewModel>({
      method: 'POST',
      url: `/auth/register`,
      body: body,
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      headers: getRequestOptions(headers, 'headers'),
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Password Reset
   * I forgot my password
   * @returns EmptyResponseViewModel Success
   * @throws ApiError
   */
  public static async passwordReset(
    {
      body,
    }: {
      body?: PasswordResetViewModel;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<EmptyResponseViewModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    delete cookies.token;
    const result = await __request<EmptyResponseViewModel>({
      method: 'POST',
      url: `/auth/password/reset`,
      body: body,
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      headers: getRequestOptions(headers, 'headers'),
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Password Update
   * Updates a password
   * @returns EmptyResponseViewModel Success
   * @throws ApiError
   */
  public static async passwordUpdate(
    {
      authorization,
      body,
    }: {
      /**
       * Authorization
       */
      authorization: string;
      body?: PasswordUpdateViewModel;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<EmptyResponseViewModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    delete cookies.token;
    const result = await __request<EmptyResponseViewModel>({
      method: 'PATCH',
      url: `/auth/password/update`,
      headers: {
        ...(authorization ? { authorization: authorization } : {}),
        ...getRequestOptions(headers, 'headers'),
      },
      body: body,
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Activities
   * Gets the auth activities
   * @returns GetActivityViewModel Success
   * @throws ApiError
   */
  public static async getActivities(
    {
      authorization,
    }: {
      /**
       * Authorization
       */
      authorization: string;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<Array<GetActivityViewModel>> {
    const { cookies = {}, headers = {} } = options ?? {};
    delete cookies.token;
    const result = await __request<Array<GetActivityViewModel>>({
      method: 'GET',
      url: `/auth/activities`,
      headers: {
        ...(authorization ? { authorization: authorization } : {}),
        ...getRequestOptions(headers, 'headers'),
      },
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }
}
